import { useRef, useMemo } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import { Printer, ArrowLeft, Loader2, FileText } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { QuizScript, QuizMediaObject } from '@quebon/shared-learning'
import {
  PrintQuizItem,
  HideQuizNumberProvider,
  PAPER_SIZES,
  mmToPx,
  KatexRenderer,
  preprocessLegacyMarkup,
  PrintQuizDescription,
  PrintQuizImage,
  getImageUrl,
  formatText,
  substituteVariables,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { getAnswerText } from './answerKeyText'

// ─── Constants ───────────────────────────────────────────────────────

const REFERENCE_TYPES = new Set(['pairing', 'image_select'])

// ─── KaTeX-aware inline text ─────────────────────────────────────────

// getImageUrl은 string|undefined 필드를 기대하므로 ContentView(null 허용)를 변환
const toImg = (c?: { signedUrl?: string | null; url?: string | null } | null) =>
  c ? { signedUrl: c.signedUrl ?? undefined, url: c.url ?? undefined } : null

// 해설 이미지: 문제 이미지(getImageUrl)와 동일하게 "프린트 버전 우선, 없으면 기존" 정책 적용.
// formatText 내부 getImageUrl은 signedUrl ?? url만 보므로, printSignedUrl을 signedUrl 자리로 끌어올린다.
const toPrintMedia = (img: QuizMediaObject): QuizMediaObject => ({
  ...img,
  signedUrl: img.printSignedUrl || img.signedUrl,
})

// 정답/해설 텍스트는 legacy markup(<frac>, 전각 기호 등)과 $$...$$ 수식을 포함할 수 있으므로
// preprocessLegacyMarkup → KatexRenderer 파이프라인으로 렌더링한다. KatexRenderer는 <span>을
// 반환하므로 인라인 레이아웃을 깨지 않는다.
function Fmt({ text, className }: { text: string; className?: string }) {
  return <KatexRenderer text={preprocessLegacyMarkup(text)} className={className} />
}

function isReferenceType(type: string): boolean {
  return REFERENCE_TYPES.has(type)
}

// ─── Sub-Components ──────────────────────────────────────────────────

function AnswerSection({ quizzes }: { quizzes: QuizScript[] }) {
  return (
    <div style={{ marginBottom: 24, breakInside: 'avoid' }}>
      {/* 정답 헤더 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #e8b4c8 0%, #d4899f 100%)',
          padding: '6px 16px',
          borderRadius: '4px 4px 0 0',
          marginBottom: 0,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>정답</span>
      </div>
      {/* 정답 그리드 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          border: '1px solid #d4899f',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          overflow: 'hidden',
        }}
      >
        {quizzes.map((quiz, idx) => (
          <div
            key={quiz.scriptId || idx}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              lineHeight: '18px',
              borderBottom: idx < quizzes.length - (quizzes.length % 3 || 3) ? '1px solid #f0d0de' : 'none',
              borderRight: (idx + 1) % 3 !== 0 ? '1px solid #f0d0de' : 'none',
            }}
          >
            <span style={{ fontWeight: 700, marginRight: 6 }}>{idx + 1}.</span>
            <Fmt text={getAnswerText(quiz)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function ExplanationSection({ quizzes }: { quizzes: QuizScript[] }) {
  return (
    <div>
      {/* 해설 헤더 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #a8d5a2 0%, #7fbf7a 100%)',
          padding: '6px 16px',
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>해설</span>
      </div>
      {/* 해설 목록 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {quizzes.map((quiz, idx) => (
          <ExplanationItem key={quiz.scriptId || idx} quiz={quiz} number={idx + 1} />
        ))}
      </div>
    </div>
  )
}

function ExplanationItem({ quiz, number }: { quiz: QuizScript; number: number }) {
  const isRefType = isReferenceType(quiz.type)
  const vars = (quiz.vars ?? undefined) as Record<string, unknown> | undefined
  const commentaryImages = (quiz.commentaryImages ?? undefined) as QuizMediaObject[] | undefined
  const printCommentaryImages = commentaryImages?.map(toPrintMedia)
  const descText = quiz.description?.text
  const descImageUrl = quiz.description?.image
    ? getImageUrl(toImg(quiz.description.image), toImg(quiz.description.imagePrint))
    : null
  const hasDescription = !!descText || !!descImageUrl

  const substitutedDesc =
    descText && vars && Object.keys(vars).length > 0
      ? substituteVariables(descText, vars)
      : descText

  return (
    <div style={{ fontSize: 13, lineHeight: '22px', breakInside: 'avoid' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ fontWeight: 700, minWidth: 20, flexShrink: 0 }}>{number}.</span>
        <div style={{ flex: 1 }}>
          {isRefType ? (
            <>
              {/* 정답이 채워진 문제 — LessonPreviewPage와 동일한 PrintQuizItem, 카드 번호는 숨김(해설 공통 번호와 중복 방지) */}
              <HideQuizNumberProvider value={true}>
                <PrintQuizItem
                  script={quiz}
                  number={number}
                  showCorrectAnswers
                  correctAnswers={quiz.correctAnswers ? quiz.correctAnswers.map(String) : undefined}
                  compact
                />
              </HideQuizNumberProvider>
              {/* 해설 텍스트 + 이미지 */}
              {hasDescription && (
                <PrintQuizDescription
                  description={quiz.description}
                  commentaryImages={printCommentaryImages}
                  vars={vars}
                  hideLabel
                  compact
                />
              )}
            </>
          ) : (
            <>
              {/* 정답 요약 (주황) */}
              <Fmt text={getAnswerText(quiz)} className="answer-key-summary" />

              {/* 해설 텍스트 (변수 치환 + {image=n} + KaTeX) */}
              {substitutedDesc && (
                <span style={{ whiteSpace: 'pre-wrap' }}>
                  {formatText(substitutedDesc, `ans-${quiz.scriptId ?? number}`, printCommentaryImages)}
                </span>
              )}

              {/* 표준 해설 이미지 */}
              {descImageUrl && <PrintQuizImage src={descImageUrl} alt="해설 이미지" className="mt-[8px]" />}

              {/* 해설 없는 경우 */}
              {!hasDescription && (
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>해설 없음</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────

export function AnswerKeyPreviewPage() {
  const { lessonId } = useParams<{ bookId: string; lessonId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)

  const lessonName = searchParams.get('name') || '레슨'
  const startPage = parseInt(searchParams.get('startPage') || '1', 10)
  const bookLabel = searchParams.get('bookLabel') || ''
  const quetureTitle = searchParams.get('quetureTitle') || lessonName

  // Fetch quizzes with answers + correctAnswers (only print-relevant locations)
  // showAnswer=false + useFixedVars=true + includeSolveResult=true:
  // - vars가 concrete 샘플값(getVarsWithSampleValueMap)으로 내려와 프론트 변수 치환이 가능해짐
  // - problem/answers/pairs는 서버에서 이미 치환됨, description은 원본(프론트에서 치환), correctAnswers 포함
  const { data: selectedQuizzes = [], isLoading: isLoading1 } = useQuery({
    queryKey: ['answer-key-quizzes', lessonId, 'selected'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'selected', false, true, true),
    enabled: !!lessonId,
  })
  const { data: blockQuizzes = [], isLoading: isLoading2 } = useQuery({
    queryKey: ['answer-key-quizzes', lessonId, 'block'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'block', false, true, true),
    enabled: !!lessonId,
  })
  const { data: appliedQuizzes = [], isLoading: isLoading3 } = useQuery({
    queryKey: ['answer-key-quizzes', lessonId, 'applied'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'applied', false, true, true),
    enabled: !!lessonId,
  })

  const isLoading = isLoading1 || isLoading2 || isLoading3
  const quizzes = useMemo(
    () => [...selectedQuizzes, ...blockQuizzes, ...appliedQuizzes],
    [selectedQuizzes, blockQuizzes, appliedQuizzes]
  )

  // Paper setup
  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${bookLabel}_${lessonName}_정답해설`,
    pageStyle: `
      @page { size: ${paper.width}mm ${paper.height}mm; margin: 16mm 14mm 18mm; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .ak-footer { display: flex !important; }
      }
    `,
  })

  return (
    <div className="flex flex-col h-full">
      {/* Top: Controls (sticky) */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <FileText size={14} />
          <span>
            <ContentMetadataText text={quetureTitle} /> · 정답과 해설 · 시작 p.{startPage} · 총 {quizzes.length}문제
          </span>
          {isLoading && (
            <span className="flex items-center gap-1 text-slate-400">
              <Loader2 size={12} className="animate-spin" /> 로딩 중...
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>
          <button
            onClick={() => reactToPrintFn()}
            disabled={quizzes.length === 0 || isLoading}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
          >
            <Printer size={16} /> PDF 다운로드
          </button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex overflow-auto flex-1 justify-center py-8 bg-slate-100">
        {isLoading ? (
          <div className="flex gap-3 items-center self-start text-slate-500">
            <Loader2 className="animate-spin" /> 퀴즈 데이터 로딩 중...
          </div>
        ) : quizzes.length === 0 ? (
          <div className="self-start text-sm text-slate-400">퀴즈가 없습니다.</div>
        ) : (
          <div
            ref={printRef}
            className="self-start bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
            style={{ width: `${pageWidthPx}px`, lineHeight: 1.6 }}
          >
            <style>{`
              .ak-content { padding: 32px 28px 48px; box-sizing: border-box; }
              .answer-key-summary { font-weight: 600; color: #b45309; margin-right: 8px; }
              .ak-footer {
                display: none;
                position: fixed;
                left: 0; right: 0; bottom: 0;
                padding: 6px 28px;
                font-size: 10px;
                color: #94a3b8;
                justify-content: space-between;
                align-items: center;
              }
              @media print {
                .ak-content { padding: 0; }
              }
            `}</style>

            <div className="ak-content">
              {/* 레슨 제목 */}
              <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '2px solid #1e293b', breakInside: 'avoid' }}>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                  <ContentMetadataText text={quetureTitle} />
                </h1>
                <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
                  정답과 해설 · p.{startPage}
                </p>
              </div>

              {/* 정답 영역 */}
              <AnswerSection quizzes={quizzes} />

              {/* 해설 영역 */}
              <ExplanationSection quizzes={quizzes} />
            </div>

            {/* 인쇄 시 모든 페이지 하단에 반복되는 브랜드 푸터 (쪽번호 없음) */}
            <div className="ak-footer">
              <span>{bookLabel}</span>
              <span>정답과 해설</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
